import { useState, useRef, forwardRef, useImperativeHandle, useMemo, useCallback, memo, useEffect } from 'react';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { Product } from '@shared/components/ui/Product';
import { StepperInput } from '@shared/components/ui/StepperInput';
import { Input } from '@shared/components/ui/input';
import { Button } from '@shared/components/ui/button';
import { Checkbox } from '@shared/components/ui/checkbox';
import { useTranslation } from 'react-i18next';
import { useProductsInfinite } from '@shared/hooks/useProductsInfinite';
import { usePacks } from '@modules/Packs';
import type { ProductItem } from '../../adapters';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBagShopping, faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';

interface ProductSelectorModalProps {
  onAddProducts?: (products: ProductItem[], shippingCosts?: number, discounts?: number, tax?: number) => void;
  onUpdateProducts?: (products: ProductItem[], shippingCosts?: number, discounts?: number, tax?: number) => void;
  currentOrderProducts?: ProductItem[];
  currentShippingCosts?: number;
  currentDiscounts?: number;
  currentTax?: number;
}

export interface ProductSelectorModalRef {
  openModal: () => void;
  closeModal: () => void;
}

export const ProductSelectorModal = memo(
  forwardRef<ProductSelectorModalRef, ProductSelectorModalProps>(
    (
      {
        onAddProducts,
        onUpdateProducts,
        currentOrderProducts = [],
        currentShippingCosts = 0,
        currentDiscounts = 0,
        currentTax = 0,
      },
      ref,
    ) => {
      const { t } = useTranslation('viewOrdersModule');
      const modalRef = useRef<ModalCenterRef>(null);
      const scrollContainerRef = useRef<HTMLDivElement>(null); // 🚀 Ref para infinite scroll
      const [isModalOpen, setIsModalOpen] = useState(false); // 🆕 Controlar si el modal está abierto
      const [selectedProducts, setSelectedProducts] = useState<ProductItem[]>([]);
      const [searchType, setSearchType] = useState<'product' | 'pack'>('product');
      const [shippingCosts, setShippingCosts] = useState(currentShippingCosts);
      const [discounts, setDiscounts] = useState(currentDiscounts);
      const [taxCosts, setTaxCosts] = useState(currentTax);
      const [searchTerm, setSearchTerm] = useState<string>('');
      const [visibleCount, setVisibleCount] = useState(15); // 🚀 Renderizar solo 15 inicialmente

      // Hook para búsqueda de productos - SOLO si el modal está abierto

      const {
        products: pagedProducts,
        total: totalProducts,
        fetchNextPage: fetchNextProducts,
        hasNextPage: hasNextProducts,
        isLoading: isLoadingProducts,
        isFetchingNextPage: isFetchingNextProducts,
      } = useProductsInfinite({
        searchTerm: isModalOpen ? searchTerm : '',
        enabled: isModalOpen,
      });

      // Hook para packs con scroll infinito - SOLO si el modal está abierto
      const {
        packs,
        fetchNextPage: fetchNextPacks,
        hasNextPage: hasNextPacks,
        total: totalPacks,
        isLoading: isLoadingPacks,
        isFetchingNextPage: isFetchingNextPacks,
      } = usePacks({
        searchTerm: isModalOpen ? searchTerm : '',
        limit: 10,
        enabled: isModalOpen,
      });

      // ⚡ OPTIMIZACIÓN: Solo procesar productos si el modal está abierto
      const allProductItems = useMemo<ProductItem[]>(() => {
        if (!isModalOpen) {
          return [];
        }

        const items = pagedProducts.map((p) => ({
          id: p.id,
          name: p.name,
          code: p.code || '',
          image: p.image || '',
          price: 0,
          quantity: 1,
          isSelected: false,
          type: 'product' as const,
          stock: p.stock,
        }));
        return items;
      }, [pagedProducts, isModalOpen]);

      // ⚡ OPTIMIZACIÓN: Solo procesar packs si el modal está abierto
      const allPackItems = useMemo<ProductItem[]>(() => {
        if (!isModalOpen) {
          return [];
        }

        const items = packs.map((pack) => ({
          id: pack.id,
          name: pack.title,
          code: `PACK-${pack.id}`,
          image: '',
          price: 0,
          quantity: 1,
          isSelected: false,
          type: 'pack' as const,
        }));
        return items;
      }, [packs, isModalOpen]);

      // Lista completa según el tipo seleccionado (memoizado)

      const availableItems = useMemo(() => {
        return searchType === 'product' ? allProductItems : allPackItems;
      }, [searchType, allProductItems, allPackItems]);

      // Filtrado local eliminado: usar directamente los resultados del servidor
      const filteredItems = useMemo(() => {
        if (!isModalOpen) return [];
        return availableItems;
      }, [availableItems, isModalOpen]);

      // Combinar items filtrados con productos ya seleccionados (memoizado)
      const displayItems = useMemo(() => {
        if (!isModalOpen) return [];

        const hasSearchTerm = searchTerm && searchTerm.trim().length > 0;
        const itemsMap = new Map<string, ProductItem>();

        // Primero agregar todos los items filtrados
        filteredItems.forEach((item) => {
          const selectedProduct = selectedProducts.find((p) => p.id === item.id && p.type === item.type);

          if (selectedProduct) {
            // Combinar item de la lista con producto seleccionado
            itemsMap.set(`${item.type}-${item.id}`, {
              ...item,
              ...selectedProduct,
              stock: item.stock || selectedProduct.stock,
            });
          } else {
            itemsMap.set(`${item.type}-${item.id}`, item);
          }
        });

        // Solo agregar productos seleccionados que NO están en filteredItems si NO hay búsqueda activa
        // Y solo si coinciden con el tipo seleccionado (product/pack)
        if (!hasSearchTerm) {
          selectedProducts.forEach((selectedProduct) => {
            const key = `${selectedProduct.type}-${selectedProduct.id}`;
            // Solo agregar si el tipo coincide con el searchType actual
            if (!itemsMap.has(key) && selectedProduct.type === searchType) {
              itemsMap.set(key, selectedProduct);
            }
          });
        }

        return Array.from(itemsMap.values());
      }, [filteredItems, selectedProducts, isModalOpen, searchTerm, searchType]);

      useImperativeHandle(
        ref,
        () => ({
          openModal: () => {
            // 🚀 Optimización: Preparar datos ANTES de abrir el modal
            const enrichedProducts = currentOrderProducts.map((product) => {
              if (product.type === 'product') {
                const productWithStock = pagedProducts.find((p) => p.id === product.id);
                if (productWithStock?.stock) {
                  return {
                    ...product,
                    stock: productWithStock.stock,
                    isSelected: true,
                  };
                }
              }
              return {
                ...product,
                isSelected: true,
              };
            });

            // 🚀 Optimización: Actualizar estados en batch
            setSelectedProducts(enrichedProducts);
            setShippingCosts(currentShippingCosts);
            setDiscounts(currentDiscounts);
            setTaxCosts(currentTax);
            setSearchTerm('');
            setVisibleCount(15); // 🚀 Reset infinite scroll a 15 items

            // 🚀 Optimización: Abrir modal DESPUÉS de preparar datos
            setIsModalOpen(true);
            modalRef.current?.openModal();
          },
          closeModal: () => {
            setIsModalOpen(false); // 🆕 Marcar modal como cerrado
            modalRef.current?.closeModal();
          },
        }),
        [currentOrderProducts, currentShippingCosts, currentDiscounts, currentTax, pagedProducts],
      );

      // Manejar selección/deselección de productos (optimizado)
      const handleToggleProduct = useCallback((item: ProductItem) => {
        setSelectedProducts((prev) => {
          const isCurrentlySelected = prev.some((p) => p.id === item.id && p.type === item.type);

          if (isCurrentlySelected) {
            // Remover de seleccionados
            return prev.filter((p) => !(p.id === item.id && p.type === item.type));
          } else {
            // Agregar a seleccionados
            return [...prev, { ...item, isSelected: true }];
          }
        });
      }, []);

      const handleQuantityChange = useCallback((productId: number, productType: string, newQuantity: number) => {
        setSelectedProducts((prev) =>
          prev.map((product) => {
            if (product.id === productId && product.type === productType) {
              return { ...product, quantity: newQuantity };
            }
            return product;
          }),
        );
      }, []);

      const handleSaveChanges = async () => {
        // Filtrar productos seleccionados y con cantidad > 0
        const validProducts = selectedProducts.filter((product) => product.isSelected && product.quantity > 0);

        // NO enviar a la API - Solo actualizar la lista local
        // El guardado a la API se hará desde el botón "Guardar cambios" principal
        if (onUpdateProducts) {
          onUpdateProducts(validProducts, shippingCosts, discounts, taxCosts);
        } else if (onAddProducts) {
          // Mantener compatibilidad con el método anterior
          const newProducts = validProducts.filter((product) => !currentOrderProducts.some((p) => p.id === product.id));
          onAddProducts(newProducts, shippingCosts, discounts, taxCosts);
        }

        setIsModalOpen(false);
        modalRef.current?.closeModal();
      };

      const handleCancel = useCallback(() => {
        setIsModalOpen(false); // 🆕 Marcar modal como cerrado
        modalRef.current?.closeModal();
      }, []);

      // 🚀 Infinite Scroll: Detectar cuando llega al final y cargar más
      const handleScroll = useCallback(() => {
        if (!scrollContainerRef.current) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

        // Disparar más tarde: 92% del scroll
        if (scrollPercentage > 0.92) {
          if (visibleCount < displayItems.length) {
            setVisibleCount((prev) => Math.min(prev + 15, displayItems.length));
          } else {
            if (searchType === 'product' && hasNextProducts) {
              fetchNextProducts();
            }
            if (searchType === 'pack' && hasNextPacks) {
              fetchNextPacks();
            }
          }
        }
      }, [
        visibleCount,
        displayItems.length,
        searchType,
        hasNextProducts,
        hasNextPacks,
        fetchNextProducts,
        fetchNextPacks,
      ]);

      // 🚀 Efecto: Resetear visibleCount cuando cambian los items (infinite scroll o nueva búsqueda)
      useEffect(() => {
        if (displayItems.length > 0) {
          setVisibleCount(15);
        }
      }, [displayItems.length]);

      // 🚀 Efecto: Resetear scroll solo cuando cambia el término de búsqueda (nueva búsqueda)
      // Usar useRef para rastrear el término anterior y solo resetear cuando realmente cambia
      const prevSearchTermRef = useRef<string>('');
      useEffect(() => {
        const currentSearchTerm = searchTerm?.trim() || '';
        const prevSearchTerm = prevSearchTermRef.current;

        // Solo resetear scroll si el término cambió (nueva búsqueda) y hay resultados
        if (currentSearchTerm !== prevSearchTerm && currentSearchTerm.length > 0 && scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = 0;
        }

        // Actualizar la referencia para la próxima vez
        prevSearchTermRef.current = currentSearchTerm;
      }, [searchTerm]);

      const selectedCount = selectedProducts.length;

      // 🚀 Estado de carga inicial - Mostrar spinner cuando se está cargando sin búsqueda
      const isInitialLoading = useMemo(() => {
        if (!isModalOpen) return false;
        const hasSearchTerm = searchTerm && searchTerm.trim().length > 0;
        // Solo mostrar loading inicial si NO hay término de búsqueda y está cargando
        if (!hasSearchTerm) {
          if (searchType === 'product') {
            // Mostrar loading si está cargando Y no hay productos de la API
            return isLoadingProducts && pagedProducts.length === 0;
          } else {
            // Mostrar loading si está cargando Y no hay packs de la API
            return isLoadingPacks && packs.length === 0;
          }
        }
        return false;
      }, [isModalOpen, searchTerm, searchType, isLoadingProducts, isLoadingPacks, pagedProducts.length, packs.length]);

      // 🚀 Estados de búsqueda - Solo mostrar "buscando" cuando se carga la primera página (page=1)
      const isSearching = useMemo(() => {
        if (!isModalOpen) return false;
        const hasSearchTerm = searchTerm && searchTerm.trim().length > 0;
        if (searchType === 'product') {
          return hasSearchTerm && isLoadingProducts; // Solo isLoadingProducts (primera página), no isFetchingNextProducts
        } else {
          return hasSearchTerm && isLoadingPacks; // Solo isLoadingPacks (primera página), no isFetchingNextPacks
        }
      }, [isModalOpen, searchTerm, searchType, isLoadingProducts, isLoadingPacks]);

      const hasNoResults = useMemo(() => {
        if (!isModalOpen) return false;
        const hasSearchTerm = searchTerm && searchTerm.trim().length > 0;
        if (!hasSearchTerm) return false;

        if (searchType === 'product') {
          return !isLoadingProducts && !isFetchingNextProducts && pagedProducts.length === 0;
        } else {
          return !isLoadingPacks && !isFetchingNextPacks && packs.length === 0;
        }
      }, [
        isModalOpen,
        searchTerm,
        searchType,
        isLoadingProducts,
        isFetchingNextProducts,
        isLoadingPacks,
        isFetchingNextPacks,
        pagedProducts.length,
        packs.length,
      ]);

      // 🚀 Items visibles para infinite scroll
      const visibleItems = useMemo(() => {
        return displayItems.slice(0, visibleCount);
      }, [displayItems, visibleCount]);

      return (
        <ModalCenter
          ref={modalRef}
          title={t('productSelector.title')}
          type="default"
          width="lg"
          fullScreenMobile={true}
        >
          <div className="flex flex-col h-full">
            {/* Contenido principal */}
            <div className="flex-1 space-y-6 max-h-[80vh] overflow-y-auto">
              {/* Barra de búsqueda con selector de tipo - Filtrar productos/packs */}
              <div className="flex items-end pt-2 gap-2">
                {/* Input de búsqueda - Filtrar productos o packs */}
                <div className="flex-1 relative">
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 md:w-3.5 md:h-3.5 text-icons pointer-events-none"
                  />
                  <Input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={
                      searchType === 'product'
                        ? t('productSelector.searchPlaceholder')
                        : t('productSelector.searchPackPlaceholder')
                    }
                    data-testid="product-search-input"
                    className="w-full pl-9 md:pl-10 text-sm placeholder:text-icons"
                  />
                </div>

                {/* Selector de tipo (Pack/Producto) */}
                <div className="w-28 md:w-40">
                  <Combobox
                    options={[
                      { label: t('productSelector.product'), value: 'product' },
                      { label: t('productSelector.pack'), value: 'pack' },
                    ]}
                    value={searchType}
                    setValue={(value) => {
                      setSearchType(value as 'product' | 'pack');
                      setSearchTerm(''); // Limpiar búsqueda al cambiar tipo
                    }}
                    placeholder={t('productSelector.selectType')}
                  />
                </div>
              </div>

              {/* Contador de resultados disponibles - Contador de productos/packs totales */}
              <div className="text-sm text-title !mt-2">
                {searchType === 'product'
                  ? t('productSelector.totalProducts', { count: totalProducts })
                  : t('productSelector.totalPacks', { count: totalPacks ?? packs.length })}
              </div>

              {/* Lista de productos disponibles */}
              <div className="space-y-4">
                {isInitialLoading ? (
                  /* Estado de carga inicial */
                  <div className="text-center py-8 text-complementary-text text-sm">
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                      <span>Cargando {searchType === 'product' ? 'productos' : 'packs'}...</span>
                    </div>
                  </div>
                ) : isSearching ? (
                  /* Estado de búsqueda */
                  <div className="text-center py-8 text-complementary-text text-sm">
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                      <span>Buscando {searchType === 'product' ? 'productos' : 'packs'}...</span>
                    </div>
                  </div>
                ) : hasNoResults ? (
                  /* Estado sin resultados */
                  <div className="text-center py-8 text-complementary-text text-sm">
                    <span>
                      No se encontraron {searchType === 'product' ? 'productos' : 'packs'} con "{searchTerm}"
                    </span>
                  </div>
                ) : displayItems.length > 0 ? (
                  <>
                    {/* Encabezados de la tabla */}
                    <div
                      className={`hidden md:grid gap-4 text-sm font-medium text-complementary-text border-b border-card-border pb-2 ${
                        searchType === 'product' ? 'grid-cols-11' : 'grid-cols-8'
                      }`}
                    >
                      <div className={`text-xs ${searchType === 'product' ? 'col-span-5' : 'col-span-5'}`}>
                        {t('productSelector.table.product')}
                      </div>
                      {searchType === 'product' && (
                        <div className="col-span-3 text-xs">{t('productSelector.availableStock')}</div>
                      )}
                      <div className={`text-xs ${searchType === 'product' ? 'col-span-3' : 'col-span-3'}`}>
                        {t('productSelector.table.quantity')}
                      </div>
                    </div>

                    {/* Lista de productos con scroll e infinite scroll */}
                    <div
                      ref={scrollContainerRef}
                      onScroll={handleScroll}
                      className="max-h-96 md:max-h-60 overflow-y-auto"
                    >
                      {visibleItems.map((item) => {
                        const isProduct = item.type === 'product';
                        const isSelected = selectedProducts.some((p) => p.id === item.id && p.type === item.type);

                        return (
                          <div key={`${item.type}-${item.id}`} className="border-b border-card-border">
                            {/* Desktop Layout */}
                            <div
                              className={`hidden md:grid gap-4 items-center py-2 ${
                                searchType === 'product' ? 'grid-cols-11' : 'grid-cols-8'
                              }`}
                            >
                              {/* Producto con checkbox */}
                              <div className={`${searchType === 'product' ? 'col-span-5' : 'col-span-5'}`}>
                                <div className="flex items-center gap-3">
                                  <Checkbox
                                    checked={isSelected}
                                    onCheckedChange={() => handleToggleProduct(item)}
                                    name={`product-${item.type}-${item.id}`}
                                  />
                                  <Product
                                    id={item.id}
                                    name={item.name}
                                    src={isProduct ? item.image : ''}
                                    code={item.code}
                                    showImageModal={isProduct && !!item.image}
                                  />
                                </div>
                              </div>

                              {/* Campo de Stock disponible - Solo para productos */}
                              {searchType === 'product' && (
                                <div className="col-span-3 flex items-center gap-2">
                                  <span className="relative inline-block">
                                    <FontAwesomeIcon icon={faBagShopping} className="w-3.5 h-3.5" />
                                    <span
                                      className={`absolute -top-1 -right-0 w-1.5 h-1.5 mt-2.5 rounded-full border border-black ${
                                        (item.stock?.disponible ?? 0) <= 0 ? 'bg-error' : 'bg-success'
                                      }`}
                                    ></span>
                                  </span>
                                  <span>{item.stock?.disponible ?? 0}</span>
                                </div>
                              )}

                              {/* Stepper de cantidad */}
                              <div className={`${searchType === 'product' ? 'col-span-3' : 'col-span-3'}`}>
                                <StepperInput
                                  value={item.quantity}
                                  onChange={(value) => handleQuantityChange(item.id, item.type, value)}
                                  min={1}
                                  max={999}
                                  className="w-full"
                                  disabled={!isSelected}
                                />
                              </div>
                            </div>
                            {/* Mobile Layout */}
                            <div className="md:hidden py-3">
                              {/* Fila principal: Checkbox + Producto + (Stock y Stepper vertical) */}
                              <div className="flex items-start gap-3">
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => handleToggleProduct(item)}
                                  name={`mobile-product-${item.type}-${item.id}`}
                                  className="mt-2"
                                />
                                <div className="flex-1 min-w-0">
                                  <Product
                                    id={item.id}
                                    name={item.name}
                                    src={isProduct ? item.image : ''}
                                    code={item.code}
                                    showImageModal={isProduct && !!item.image}
                                  />
                                </div>
                                {/* Stock y Stepper apilados verticalmente a la derecha */}
                                <div className="flex flex-col gap-2 items-end">
                                  {/* Stock disponible - Solo para productos */}
                                  {isProduct && (
                                    <div className="flex items-center gap-2">
                                      <span className="relative inline-block">
                                        <FontAwesomeIcon icon={faBagShopping} className="w-3.5 h-3.5" />
                                        <span
                                          className={`absolute -top-1 -right-0 w-1.5 h-1.5 mt-2.5 rounded-full border border-black ${
                                            (item.stock?.disponible ?? 0) <= 0 ? 'bg-error' : 'bg-success'
                                          }`}
                                        ></span>
                                      </span>
                                      <span className="text-sm">{item.stock?.disponible ?? 0}</span>
                                    </div>
                                  )}
                                  {/* Stepper debajo del stock */}
                                  <StepperInput
                                    value={item.quantity}
                                    onChange={(value) => handleQuantityChange(item.id, item.type, value)}
                                    min={1}
                                    max={999}
                                    disabled={!isSelected}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {/* Indicador de carga para infinite scroll */}
                      {visibleCount < displayItems.length && (
                        <div className="text-center py-4 text-complementary-text text-xs">
                          Mostrando {visibleCount} de {displayItems.length} productos...
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  /* Estado vacío (sin búsqueda) */
                  <div className="text-center py-8 text-complementary-text text-xs">
                    {t('productSelector.emptyState')}
                  </div>
                )}
              </div>
            </div>

            {/* Botones de acción - Fuera del scroll, siempre visibles */}
            <div className="flex justify-end gap-3 pt-4 border-t border-card-border mt-4">
              <Button variant="outline" onClick={handleCancel} size="lg">
                {t('productSelector.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveChanges}
                disabled={selectedCount === 0}
                className="flex items-center gap-2"
                size="lg"
              >
                {t('productSelector.add')}
              </Button>
            </div>
          </div>
        </ModalCenter>
      );
    },
  ),
);

ProductSelectorModal.displayName = 'ProductSelectorModal';
